import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { getAuthHeaders, useAuth } from '../contexts/AuthContext';
import { useCountry } from '../contexts/CountryContext';
import { canEditModule } from '../lib/permissions';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Rocket, X, Barcode, FileUp, Download, Package } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { PlatformIdentifierManagerDialog } from '../components/PlatformIdentifierManagerDialog';
import { BulkIdentifierImportDialog } from '../components/BulkIdentifierImportDialog';
import { SKUMasterUploadDialog } from '../components/SKUMasterUploadDialog';
import { EMPTY_SKU_FILTERS, SkuFilters } from '../components/skus/SkuFilters';
import { SkuPurchaseHistory } from '../components/skus/SkuPurchaseHistory';
import { SalesPager, SalesTableSkeleton } from '../components/sales/DailySalesTable';
import { queryString, useAbortableGet, useDebouncedValue } from '../components/sales/useAbortableGet';
import { downloadFile } from '../lib/download';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { errorMessage } from '../components/LoadError';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SKU_GRID_COLUMNS = '72px 130px minmax(260px, 1.8fr) 110px 130px 60px 115px 110px 105px 105px 120px';
// Without the Cost column (non-admins never see purchase cost).
const SKU_GRID_COLUMNS_NO_COST = '72px 130px minmax(260px, 1.8fr) 110px 130px 60px 115px 110px 105px 120px';

const TIER_TONE = { A: 'bg-green-100 text-green-700', B: 'bg-orange-100 text-orange-700' };

// Numeric SKU form fields. Values are kept as the raw input string while typing
// (so a field can be cleared) and parsed/validated only on save.
const NUMERIC_FIELDS = {
  marketplace_buffer_days: { label: 'Marketplace Buffer', unit: 'days', integer: true, min: 0, hint: 'Extra days of stock to keep at marketplace warehouses (FBA, FC).' },
  cost_price: { label: 'Cost Price', min: 0 },
  selling_price: { label: 'Selling Price', min: 0 },
  lead_time_days: { label: 'Lead Time', unit: 'days', integer: true, min: 0, hint: 'Days from placing a PO to stock arriving.' },
  moq: { label: 'MOQ', unit: 'pcs', integer: true, min: 1, hint: 'Minimum order quantity per PO, in pieces.' },
  buffer_days: { label: 'Warehouse Buffer', unit: 'days', integer: true, min: 0, hint: 'Safety stock days at your own warehouse used for reorders.' },
};
const EMPTY_SKU_FORM = {
  sku_code: '',
  product_name: '',
  brand: '',
  category: '',
  subcategory: '',
  color: '',
  tier: 'C',
  channel_mode: 'BOTH',
  marketplace_buffer_days: '14',
  cost_price: '0',
  selling_price: '0',
  supplier: '',
  lead_time_days: '30',
  moq: '1',
  buffer_days: '7',
  image_url: '',
  gst_pct: '',
  hsn_code: '',
  weight_kg: '',
};

// Optional profitability fields: blank means "not set" (Profitability then uses the category / default GST).
const OPTIONAL_TEXT_FIELDS = ['gst_pct', 'hsn_code', 'weight_kg'];

const parseOptionalFields = (form) => {
  const errors = {};
  const values = {};
  const gst = String(form.gst_pct ?? '').trim();
  if (gst === '') values.gst_pct = null;
  else if (!Number.isFinite(Number(gst)) || Number(gst) < 0 || Number(gst) > 100) errors.gst_pct = 'Enter 0 to 100';
  else values.gst_pct = Number(gst);
  const weight = String(form.weight_kg ?? '').trim();
  if (weight === '') values.weight_kg = null;
  else if (!Number.isFinite(Number(weight)) || Number(weight) < 0) errors.weight_kg = 'Enter a weight of 0 or more';
  else values.weight_kg = Number(weight);
  const hsn = String(form.hsn_code ?? '').trim();
  if (hsn === '') values.hsn_code = null;
  else if (!/^[0-9]{4,10}$/.test(hsn)) errors.hsn_code = '4 to 10 digits';
  else values.hsn_code = hsn;
  return { errors, values };
};

/** Parse the numeric form strings. Returns { errors, values } — errors keyed by field. */
const parseSkuNumbers = (form) => {
  const errors = {};
  const values = {};
  Object.entries(NUMERIC_FIELDS).forEach(([key, spec]) => {
    const raw = String(form[key] ?? '').trim();
    if (raw === '') { errors[key] = 'Required'; return; }
    const num = Number(raw);
    if (!Number.isFinite(num)) errors[key] = 'Enter a number';
    else if (spec.integer && !Number.isInteger(num)) errors[key] = 'Enter a whole number';
    else if (num < spec.min) errors[key] = spec.min === 0 ? 'Cannot be negative' : `Must be at least ${spec.min}`;
    else values[key] = num;
  });
  return { errors, values };
};

// The SKU form uses noValidate, so required text fields are checked here alongside the numbers.
const validateSkuForm = (form) => {
  const numbers = parseSkuNumbers(form);
  const optional = parseOptionalFields(form);
  const errors = { ...numbers.errors, ...optional.errors };
  const values = { ...numbers.values, ...optional.values };
  if (!String(form.sku_code ?? '').trim()) errors.sku_code = 'Required';
  if (!String(form.product_name ?? '').trim()) errors.product_name = 'Required';
  return { errors, values };
};

const identifierCount = (rows) => {
  if (Array.isArray(rows)) return rows.length;
  return rows ? 1 : 0;
};

const SkuNumberField = ({ field, value, error, warning, onChange, currency }) => {
  const spec = NUMERIC_FIELDS[field];
  const id = `sku-form-${field}`;
  const isMoney = !spec.integer;
  const describedBy = error ? `${id}-error` : (warning || spec.hint) ? `${id}-hint` : undefined;
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-[13px] font-medium text-gray-700">
        {spec.label} ({isMoney ? currency : spec.unit})
      </Label>
      <Input
        id={id}
        type="number"
        inputMode={isMoney ? 'decimal' : 'numeric'}
        step={isMoney ? '0.01' : '1'}
        min={spec.min}
        value={value}
        onChange={(e) => onChange(field, e.target.value)}
        aria-invalid={Boolean(error)}
        aria-describedby={describedBy}
        className={`rounded-lg ${error ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
        data-testid={`sku-${field.replace(/_/g, '-')}-input`}
      />
      {error ? (
        <p id={`${id}-error`} className="text-[12px] font-medium text-red-700" data-testid={`sku-${field.replace(/_/g, '-')}-error`}>{error}</p>
      ) : warning ? (
        <p id={`${id}-hint`} className="text-[12px] font-medium text-amber-800">{warning}</p>
      ) : spec.hint ? (
        <p id={`${id}-hint`} className="text-[12px] text-gray-500">{spec.hint}</p>
      ) : null}
    </div>
  );
};

export const SKUMasterPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const canEditSkus = canEditModule(user, 'skus');
  const isAdmin = user?.role === 'admin';
  const { country, currency } = useCountry();
  // ?q=CODE (e.g. from the Ctrl+K search) opens the list already searched
  const [filters, setFilters] = useState(() => ({ ...EMPTY_SKU_FILTERS, search: searchParams.get('q') || '' }));
  const urlQuery = searchParams.get('q');
  useEffect(() => {
    if (urlQuery != null) setFilters(prev => (prev.search === urlQuery ? prev : { ...EMPTY_SKU_FILTERS, search: urlQuery }));
  }, [urlQuery]);
  const [sortBy, setSortBy] = useState('sku_code');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [refreshKey, setRefreshKey] = useState(0);
  const [exporting, setExporting] = useState(false);
  const search = useDebouncedValue(filters.search.trim(), 350);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSku, setEditingSku] = useState(null);
  const [identifierSku, setIdentifierSku] = useState(null);
  const [bulkIdentifierOpen, setBulkIdentifierOpen] = useState(false);
  const [masterUploadOpen, setMasterUploadOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // SKU awaiting delete confirmation
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [showNewLaunchOnly, setShowNewLaunchOnly] = useState(searchParams.get('filter') === 'new_launch');
  const [formData, setFormData] = useState(EMPTY_SKU_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [renameConfirmOpen, setRenameConfirmOpen] = useState(false);
  // Captured when the rename dialog opens so its title stays stable while it animates out.
  const [renameTarget, setRenameTarget] = useState({ from: '', to: '' });

  const filterQuery = queryString({ ...filters, search, new_launch: showNewLaunchOnly ? 'true' : '', country });
  const { data, loading, error } = useAbortableGet(
    `${API}/skus/catalogue?${filterQuery}&${queryString({ sort_by: sortBy, sort_order: sortBy === 'created_at' ? 'desc' : 'asc', page, page_size: pageSize })}`, refreshKey);
  const skus = data?.rows || [];
  const fetchSkus = useCallback(() => setRefreshKey(k => k + 1), []);

  useEffect(() => { if (error) toast.error(error); }, [error]);

  // Check URL params on mount
  const requestedFilter = searchParams.get('filter');
  useEffect(() => {
    if (requestedFilter === 'new_launch') {
      setShowNewLaunchOnly(true);
    }
  }, [requestedFilter]);

  // ?create=CODE opens "Create SKU" with the code filled in (used from Purchases)
  const createCode = searchParams.get('create');
  useEffect(() => {
    if (!createCode) return;
    setEditingSku(null);
    setFormData(prev => ({ ...prev, sku_code: createCode }));
    setDialogOpen(true);
  }, [createCode]);

  const changeFilters = next => { setFilters(next); setPage(1); };

  const handleExport = async () => {
    setExporting(true);
    try {
      await downloadFile(`${API}/skus/catalogue/export?${filterQuery}`, `SKU_MASTER_EXPORT_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success(`Exported ${(data?.total || 0).toLocaleString('en-IN')} SKUs`);
    } catch (err) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const clearNewLaunchFilter = () => {
    setShowNewLaunchOnly(false);
    setSearchParams({});
    setPage(1);
  };

  const saveSku = async () => {
    const { errors, values } = validateSkuForm(formData);
    if (Object.keys(errors).length) {
      setFormErrors(errors);
      toast.error('Fix the highlighted fields before saving');
      throw new Error('invalid');
    }
    const payload = { ...formData, ...values, country };
    setSaving(true);
    try {
      if (editingSku) {
        await axios.put(`${API}/skus/${encodeURIComponent(editingSku.sku_code)}`, payload, { headers: getAuthHeaders() });
        toast.success('SKU updated successfully');
      } else {
        await axios.post(`${API}/skus`, payload, { headers: getAuthHeaders() });
        toast.success('SKU created successfully');
      }
      setDialogOpen(false);
      resetForm();
      if (createCode) setSearchParams({});
      fetchSkus();
    } catch (error) {
      toast.error(editingSku ? 'Could not update SKU' : 'Could not create SKU', { description: errorMessage(error) });
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const renaming = Boolean(editingSku && formData.sku_code.trim() && formData.sku_code.trim() !== editingSku.sku_code);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { errors } = validateSkuForm(formData);
    if (Object.keys(errors).length) {
      setFormErrors(errors);
      return;
    }
    if (renaming) {
      setRenameTarget({ from: editingSku.sku_code, to: formData.sku_code.trim() });
      setRenameConfirmOpen(true);
      return;
    }
    try { await saveSku(); } catch { /* reported in saveSku */ }
  };

  const handleDelete = (sku) => {
    setDeleteTarget(sku);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    const sku_code = deleteTarget?.sku_code;
    try {
      await axios.delete(`${API}/skus/${encodeURIComponent(sku_code)}?country=${encodeURIComponent(country)}`, { headers: getAuthHeaders() });
      toast.success(`SKU ${sku_code} deleted`);
      fetchSkus();
    } catch (error) {
      toast.error(`Failed to delete SKU ${sku_code}`, { description: errorMessage(error) });
      throw error; // keep the dialog open
    }
  };

  const resetForm = () => {
    setFormData(EMPTY_SKU_FORM);
    setFormErrors({});
    setEditingSku(null);
  };

  const openEditDialog = (sku) => {
    setEditingSku(sku);
    const next = { ...sku };
    // cost_price is not sent to non-admins; the server ignores their cost anyway, so default it to pass validation.
    Object.keys(NUMERIC_FIELDS).forEach((key) => { next[key] = sku[key] == null ? (key === 'cost_price' ? '0' : '') : String(sku[key]); });
    OPTIONAL_TEXT_FIELDS.forEach((key) => { next[key] = sku[key] == null ? '' : String(sku[key]); });
    setFormData(next);
    setFormErrors({});
    setDialogOpen(true);
  };

  const setField = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    if (formErrors[key]) setFormErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="sku-master-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">SKU Master</h1>
          <p className="mt-1 text-[15px] text-gray-500">Manage product catalog</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={handleExport} disabled={exporting} className="h-9 text-[13px]" data-testid="export-sku-master-button">
            <Download className="mr-2 h-4 w-4" />{exporting ? 'Exporting…' : 'Export'}
          </Button>
          {canEditSkus && (
            <>
              <Button variant="outline" onClick={() => setMasterUploadOpen(true)} className="h-9 text-[13px]" data-testid="upload-sku-master-button">
                <FileUp className="mr-2 h-4 w-4" />Upload Master File
              </Button>
              <Button variant="outline" onClick={() => setBulkIdentifierOpen(true)} className="h-9 text-[13px]" data-testid="bulk-import-identifiers-button">
                <FileUp className="mr-2 h-4 w-4" />Bulk Import Identifiers
              </Button>
            </>
          )}
        {canEditSkus && (
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreateDialog} className="h-9 px-4 bg-[#007AFF] hover:bg-[#0066CC] text-white text-[13px] font-medium rounded-lg" data-testid="add-sku-button">
              <Plus className="h-4 w-4 mr-2" /> Add SKU
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl">
            <DialogHeader>
              <DialogTitle className="text-[17px] font-semibold">{editingSku ? 'Edit SKU' : 'Create New SKU'}</DialogTitle>
              <DialogDescription>Maintain the core product record used across inventory and marketplace operations.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} noValidate className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="sku-form-code" className="text-[13px] font-medium text-gray-700">SKU Code *</Label>
                  <Input
                    id="sku-form-code"
                    value={formData.sku_code}
                    onChange={(e) => setField('sku_code', e.target.value)}
                    required
                    aria-invalid={Boolean(formErrors.sku_code)}
                    aria-describedby={formErrors.sku_code ? 'sku-form-code-error' : undefined}
                    disabled={editingSku !== null && !isAdmin}
                    className={`rounded-lg ${formErrors.sku_code ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                    data-testid="sku-code-input"
                  />
                  {formErrors.sku_code && (
                    <p id="sku-form-code-error" className="text-[12px] font-medium text-red-700" data-testid="sku-code-error">{formErrors.sku_code}</p>
                  )}
                  {editingSku && isAdmin && (
                    <p className={`text-[12px] ${renaming ? 'font-medium text-amber-800' : 'text-gray-600'}`} data-testid="sku-rename-hint">
                      {renaming ? `Renaming ${editingSku.sku_code} → ${formData.sku_code.trim()} updates stock, sales, PO and purchase history. You'll be asked to confirm.` : 'Admins can rename the code; history moves with it.'}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sku-form-name" className="text-[13px] font-medium text-gray-700">Product Name *</Label>
                  <Input
                    id="sku-form-name"
                    value={formData.product_name}
                    onChange={(e) => setField('product_name', e.target.value)}
                    required
                    aria-invalid={Boolean(formErrors.product_name)}
                    aria-describedby={formErrors.product_name ? 'sku-form-name-error' : undefined}
                    className={`rounded-lg ${formErrors.product_name ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                    data-testid="product-name-input"
                  />
                  {formErrors.product_name && (
                    <p id="sku-form-name-error" className="text-[12px] font-medium text-red-700" data-testid="product-name-error">{formErrors.product_name}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-[13px] font-medium text-gray-700">Brand</Label>
                  <Input
                    value={formData.brand}
                    onChange={(e) => setFormData({...formData, brand: e.target.value})}
                    className="rounded-lg"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[13px] font-medium text-gray-700">Category</Label>
                  <Input
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="rounded-lg"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[13px] font-medium text-gray-700">Color</Label>
                  <Input
                    value={formData.color}
                    onChange={(e) => setFormData({...formData, color: e.target.value})}
                    className="rounded-lg"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sku-form-tier" className="text-[13px] font-medium text-gray-700">Tier</Label>
                  <Select value={formData.tier} onValueChange={(val) => setFormData({...formData, tier: val})}>
                    <SelectTrigger id="sku-form-tier" className="rounded-lg">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {/* Same tiers as the SKU filter and the master-file import (A–D). */}
                      {['A', 'B', 'C', 'D'].map(t => <SelectItem key={t} value={t}>Tier {t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-[13px] font-medium text-gray-700">Channel Mode</Label>
                  <Select value={formData.channel_mode} onValueChange={(val) => setFormData({...formData, channel_mode: val})}>
                    <SelectTrigger className="rounded-lg">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SELLER_ONLY">Seller Only</SelectItem>
                      <SelectItem value="RETAIL_ONLY">Retail Only</SelectItem>
                      <SelectItem value="BOTH">Both</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <SkuNumberField field="marketplace_buffer_days" value={formData.marketplace_buffer_days} error={formErrors.marketplace_buffer_days} onChange={setField} currency={currency} />
                {isAdmin && <SkuNumberField field="cost_price" value={formData.cost_price} error={formErrors.cost_price} onChange={setField} currency={currency} />}
                <SkuNumberField field="selling_price" value={formData.selling_price} error={formErrors.selling_price} onChange={setField} currency={currency}
                  warning={isAdmin && Number(formData.selling_price) > 0 && Number(formData.selling_price) < Number(formData.cost_price) ? 'Selling price is below cost.' : ''} />
                <div className="space-y-2">
                  <Label htmlFor="sku-form-gst_pct" className="text-[13px] font-medium text-gray-700">GST % (on selling price)</Label>
                  <Input id="sku-form-gst_pct" inputMode="decimal" value={formData.gst_pct} onChange={(e) => setField('gst_pct', e.target.value)}
                    aria-invalid={Boolean(formErrors.gst_pct)} className={`rounded-lg ${formErrors.gst_pct ? 'border-red-500' : ''}`} data-testid="sku-gst-pct-input" />
                  {formErrors.gst_pct
                    ? <p className="text-[12px] font-medium text-red-700">{formErrors.gst_pct}</p>
                    : <p className="text-[12px] text-gray-500">e.g. 18. Blank = category / default rate.</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sku-form-hsn_code" className="text-[13px] font-medium text-gray-700">HSN code</Label>
                  <Input id="sku-form-hsn_code" inputMode="numeric" value={formData.hsn_code} onChange={(e) => setField('hsn_code', e.target.value)}
                    aria-invalid={Boolean(formErrors.hsn_code)} className={`rounded-lg ${formErrors.hsn_code ? 'border-red-500' : ''}`} data-testid="sku-hsn-code-input" />
                  {formErrors.hsn_code
                    ? <p className="text-[12px] font-medium text-red-700">{formErrors.hsn_code}</p>
                    : <p className="text-[12px] text-gray-500">Optional, 4 to 10 digits.</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sku-form-weight_kg" className="text-[13px] font-medium text-gray-700">Weight per unit (kg)</Label>
                  <Input id="sku-form-weight_kg" inputMode="decimal" value={formData.weight_kg} onChange={(e) => setField('weight_kg', e.target.value)}
                    aria-invalid={Boolean(formErrors.weight_kg)} className={`rounded-lg ${formErrors.weight_kg ? 'border-red-500' : ''}`} data-testid="sku-weight-kg-input" />
                  {formErrors.weight_kg
                    ? <p className="text-[12px] font-medium text-red-700">{formErrors.weight_kg}</p>
                    : <p className="text-[12px] text-gray-500">Packed weight. Used for weight-based shipping fees.</p>}
                </div>
                <div className="space-y-2">
                  <Label className="text-[13px] font-medium text-gray-700">Supplier</Label>
                  <Input
                    value={formData.supplier}
                    onChange={(e) => setFormData({...formData, supplier: e.target.value})}
                    className="rounded-lg"
                  />
                </div>
                <SkuNumberField field="lead_time_days" value={formData.lead_time_days} error={formErrors.lead_time_days} onChange={setField} currency={currency} />
                <SkuNumberField field="moq" value={formData.moq} error={formErrors.moq} onChange={setField} currency={currency} />
                <SkuNumberField field="buffer_days" value={formData.buffer_days} error={formErrors.buffer_days} onChange={setField} currency={currency} />
                {editingSku && <div className="sm:col-span-2"><SkuPurchaseHistory skuCode={editingSku.sku_code} country={country} /></div>}
                <div className="space-y-2 sm:col-span-2">
                  <Label className="text-[13px] font-medium text-gray-700">Image URL</Label>
                  <Input
                    value={formData.image_url}
                    onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                    placeholder="https://..."
                    className="rounded-lg"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-lg">Cancel</Button>
                <Button type="submit" disabled={saving} className="bg-[#007AFF] hover:bg-[#0066CC] rounded-lg" data-testid="save-sku-button">{saving ? 'Saving…' : 'Save SKU'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        )}
        </div>
      </div>

      <Card className="p-4 xl:p-5 bg-white rounded-2xl border-0 shadow-sm space-y-3">
        <SkuFilters showCost={isAdmin} filters={filters} onChange={changeFilters} facets={data?.facets} sort={sortBy} onSort={value => { setSortBy(value); setPage(1); }} />
        <div className="flex items-center gap-3">
          {showNewLaunchOnly && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-[12px] font-medium">
              <Rocket className="h-3.5 w-3.5" />
              New Launches (Last 30 Days)
              <button onClick={clearNewLaunchFilter} className="hover:bg-blue-100 rounded-full p-0.5" aria-label="Clear new launch filter" data-testid="clear-new-launch-filter">
                <X className="h-3 w-3" aria-hidden="true" />
              </button>
            </div>
          )}
          <div className="ml-auto text-[12px] text-gray-500" data-testid="sku-count">
            {data ? `${data.total.toLocaleString('en-IN')} of ${data.catalogue_total.toLocaleString('en-IN')} SKUs` : 'Loading…'}
          </div>
        </div>
      </Card>

      <Card className="bg-white rounded-2xl border-0 shadow-sm overflow-hidden">
        {loading && !data ? (
          <SalesTableSkeleton rows={10} />
        ) : (
          <div className={`overflow-x-auto transition-opacity ${loading ? 'opacity-50' : ''}`} role="table" aria-label="SKU Master" data-testid="sku-table">
            <div className="min-w-[1220px]">
              <div className="grid bg-gray-50" style={{ gridTemplateColumns: isAdmin ? SKU_GRID_COLUMNS : SKU_GRID_COLUMNS_NO_COST }} role="row">
                {['Image', 'SKU Code', 'Product Name', 'Brand', 'Category', 'Tier', 'Channel', 'Mappings', ...(isAdmin ? ['Cost'] : []), 'Price', 'Actions'].map((heading, index) => (
                  <div key={heading} role="columnheader" className={`p-4 xl:p-5 text-[12px] xl:text-[13px] font-semibold text-gray-500 uppercase tracking-wide ${index >= 8 ? 'text-right' : 'text-left'}`}>{heading}</div>
                ))}
              </div>
              <div className="divide-y divide-gray-100" role="rowgroup">
                {skus.map((sku, index) => (
                  <div key={sku.sku_code} role="row" className="grid items-center hover:bg-gray-50 transition-colors" style={{ gridTemplateColumns: isAdmin ? SKU_GRID_COLUMNS : SKU_GRID_COLUMNS_NO_COST }} data-testid={`sku-row-${index}`}>
                    <div role="cell" className="p-4">
                      {sku.image_url ? (
                        <img
                          src={sku.image_url}
                          alt={sku.product_name || sku.sku_code}
                          loading="lazy"
                          className="w-12 h-12 object-cover rounded-lg"
                        />
                      ) : (
                        <div role="img" aria-label="No image" title="No image" className="flex h-12 w-12 items-center justify-center rounded-lg bg-gray-100 text-gray-500" data-testid={`sku-no-image-${index}`}>
                          <Package className="h-5 w-5" aria-hidden="true" />
                        </div>
                      )}
                    </div>
                    <div role="cell" className="p-4 font-mono text-[13px] font-medium text-black">
                      {sku.sku_code}
                      {sku.bundle_of && (
                        <span className="mt-1 block w-fit rounded-md bg-emerald-50 px-1.5 py-0.5 font-sans text-[10px] font-semibold text-emerald-700" data-testid={`sku-bundle-badge-${index}`}>
                          Bundle · {sku.bundle_qty} × {sku.bundle_of}
                        </span>
                      )}
                    </div>
                    <div role="cell" className="p-4 text-[13px] text-gray-700 line-clamp-2">{sku.product_name}</div>
                    <div role="cell" className="p-4 text-[13px] text-gray-600 truncate">{sku.brand || '-'}</div>
                    <div role="cell" className="p-4 text-[13px] text-gray-600 truncate">{sku.category || '-'}</div>
                    <div role="cell" className="p-4">
                      <span className={`px-2 py-1 rounded-lg text-[11px] font-semibold ${TIER_TONE[sku.tier] || 'bg-gray-100 text-gray-600'}`}>
                        {sku.tier}
                      </span>
                    </div>
                    <div role="cell" className="p-4 text-[11px] text-gray-500 break-words">{sku.channel_mode}</div>
                    <div role="cell" className="p-4">
                      {(() => {
                        const groups = Object.values(sku.platform_identifiers || {});
                        const count = groups.reduce((sum, rows) => sum + identifierCount(rows), 0);
                        return count > 0 ? (
                          <div data-testid={`sku-mapping-summary-${index}`}>
                            <p className="text-[12px] font-semibold text-gray-800">{count} identifier{count === 1 ? '' : 's'}</p>
                            <p className="text-[11px] text-gray-500">{groups.length} platform{groups.length === 1 ? '' : 's'}</p>
                          </div>
                        ) : <span className="text-[11px] text-gray-500" data-testid={`sku-mapping-empty-${index}`}>Not mapped</span>;
                      })()}
                    </div>
                    {isAdmin && <div role="cell" className="p-4 text-right font-mono text-[13px]">₹{Number(sku.cost_price || 0).toFixed(2)}</div>}
                    <div role="cell" className="p-4 text-right font-mono text-[13px] font-medium">₹{Number(sku.selling_price || 0).toFixed(2)}</div>
                    <div role="cell" className="p-4 text-right">
                      <div className="flex justify-end gap-1">
                        {canEditSkus && (
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => setIdentifierSku(sku)}
                          className="h-8 w-8 rounded-lg text-blue-600 hover:bg-blue-50 hover:text-blue-700"
                          aria-label={`Manage marketplace identifiers for ${sku.sku_code}`}
                          title="Manage marketplace identifiers"
                          data-testid={`sku-${sku.sku_code}-manage-identifiers-btn`}
                        >
                          <Barcode className="h-4 w-4" aria-hidden="true" />
                        </Button>
                        )}
                        {canEditSkus && (
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => openEditDialog(sku)}
                          className="h-8 w-8 rounded-lg hover:bg-gray-100"
                          aria-label={`Edit ${sku.sku_code}`}
                          title={`Edit ${sku.sku_code}`}
                          data-testid={`edit-sku-${index}`}
                        >
                          <Edit2 className="h-4 w-4 text-gray-500" aria-hidden="true" />
                        </Button>
                        )}
                        {canEditSkus && (
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleDelete(sku)}
                          className="h-8 w-8 rounded-lg hover:bg-red-50"
                          aria-label={`Delete ${sku.sku_code}`}
                          title={`Delete ${sku.sku_code}`}
                          data-testid={`delete-sku-${index}`}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" aria-hidden="true" />
                        </Button>
                        )}
                        {!canEditSkus && <span className="text-[11px] text-gray-500">View only</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {skus.length === 0 && (
              <div className="text-center py-12 text-muted-foreground" data-testid="sku-empty">
                {data?.catalogue_total ? 'No SKUs match these filters' : 'No SKUs yet. Create your first SKU to get started.'}
              </div>
            )}
          </div>
        )}
        <SalesPager prefix="sku" page={page} pageSize={pageSize} total={data?.total || 0} loading={loading}
          onPage={setPage} onPageSize={size => { setPageSize(size); setPage(1); }} />
      </Card>
      <PlatformIdentifierManagerDialog
        open={Boolean(identifierSku)}
        onOpenChange={open => !open && setIdentifierSku(null)}
        sku={identifierSku}
        country={country}
        onUpdated={fetchSkus}
      />
      <BulkIdentifierImportDialog
        open={bulkIdentifierOpen}
        onOpenChange={setBulkIdentifierOpen}
        country={country}
        onImported={fetchSkus}
      />
      <SKUMasterUploadDialog
        open={masterUploadOpen}
        onOpenChange={setMasterUploadOpen}
        country={country}
        onImported={fetchSkus}
      />
      <ConfirmDialog
        open={renameConfirmOpen}
        onOpenChange={setRenameConfirmOpen}
        title={`Rename ${renameTarget.from} to ${renameTarget.to}?`}
        description="Stock, sales, Retail PO, purchase, return, alert and code-mapping records will all move to the new code. Files that still use the old code will need a code mapping."
        confirmLabel="Rename and save"
        busyLabel="Saving…"
        onConfirm={saveSku}
        testId="rename-sku-confirm"
      />
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={`Delete SKU ${deleteTarget?.sku_code || ''}?`}
        description={`${deleteTarget?.product_name ? `${deleteTarget.product_name} (${country}) ` : `This SKU (${country}) `}will be removed from the SKU master. This cannot be undone.`}
        confirmLabel="Delete SKU"
        busyLabel="Deleting…"
        destructive
        onConfirm={confirmDelete}
        testId="delete-sku-confirm"
      />
    </div>
  );
};